import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CellContext } from "@tanstack/react-table";
import React, {
  ChangeEvent,
  ChangeEventHandler,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Button } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import noMessageIcon from "../../assets/Images/svg/018-empty.svg";
import { ActionType } from "../../constants/enums/action-type-enum";
import { ColumnType } from "../../constants/enums/grid-column-type-enum";
import { ModuleType } from "../../constants/enums/module-type-enum";
import userSettingEnum from "../../constants/enums/user-setting-enum";
import userTypeEnum from "../../constants/enums/user-type-enum";
import { useStore } from "../../contexts/store-provider";
import { isOperationPermittedRoleBase } from "../../helpers/action-permission-helper";
import {
  getBusinessTypeIdFromLocalStorage,
  getUserTypeFromLocalStorage,
} from "../../helpers/localstorage-helper";
import IPageInfo, { IPageLimitInfo } from "../../models/ICommon";
import { ISorting } from "../../models/ISorting";
import { IHeadquarterVM } from "../../models/response/IHeadquarterResponse";
import LoaderButton from "../../shared-components/button/loader-button";
import FormLoader from "../../shared-components/FormLoader/FormLoader";
import Grid, { getColumns } from "../../shared-components/Grid/Grid";
import Pagination from "../../shared-components/Grid/Pagination";
import DropdownInput from "../../shared-components/input/DropdownInput";
import LanguageSlider from "../../shared-components/language-slider/language-slider";
import { formatMessage } from "../../translations/format-message";
import { observer } from "mobx-react";
import AddEditHeadquarter from "./components/add-edit-headquarter";
import AddPopupHOC from "../../shared-components/popup/add/add-popup-hoc";
import ChangePassword from "../user/components/change-password";
import base64 from "base-64";
import ChangePasswordPopupHOC from "../../shared-components/popup/change-password/change-password-popup-hoc";
import { safeBase64Encode } from "../../constants/constants";

const HeadQuarterList: React.FC = (props: any) => {
  const {
    storeManagementStore,
    preferencesStore,
    masterManagementStore,
    headquarterStore,
    userStore,
  } = useStore();

  const [openPanel, setOpenPanel] = useState(false);

  const { UpdateHeadquarterPasswordService } = userStore;
  const {
    getAllHeadquartertListState,
    GetAllHeadquarterListService,
    headquarterList,
    allHeadquarterList,
    addUpdateHeadquartertState,
    deleteHeadquarterByIdState,
    resetAddUpdateHeadquarter,
    resetGetHeadquarterDetail,
    resetHeadquarterList,
    resetDeleteHeadquarter,
    getHeadquarterFilter,
    setHeadquarterFilterDetail,
    resetHeadquarterFilterDetail,
  } = headquarterStore;

  const {
    userSetting,
    setUserSetting,
    islanguageChanged,
    updateLanguageChangedFlag,
  } = preferencesStore;
  const { GetAllBusinessTypeService, getAllBusinessTypesAll } =
    masterManagementStore;
  const [businessTypeSearch, setBusinessTypeSearch] = useState(-1);

  const handleBusinessTypeSearch: ChangeEventHandler<HTMLSelectElement> =
    useCallback((event: ChangeEvent<HTMLSelectElement>) => {
      setBusinessTypeSearch(event.target.value as unknown as number);
      setHeadquarterFilterDetail({
        BusinessTypeId: Number(event.target.value as unknown as number),
        IsFilterActive: Number(event.target.value) === -1 ? false : true,
      });
      setPageInfo({...pageInfo, currentPage: 1, isRefreshPage: true});
    }, []);

  const [disablePagination, setDisablePagination] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<number>(0);
  const pageLimitOptions: Array<number> = [10, 50, 100];
  const [pageLimitInfo, setPageLimit] = useState<IPageLimitInfo>({
    pageLimit: userSetting.pageLimitConfig,
    isPageLimitChange: false,
  });

  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
    isPagerChange: false,
    isRefreshPage: false,
  });
  const [sorting, setSorting] = useState<ISorting>({
    column: "store_name",
    orderBy: "asc",
  });

  const userType = getUserTypeFromLocalStorage();
  const businessTypeId = getBusinessTypeIdFromLocalStorage();

  const onPageChanged = (pageNumber: number) => {
    setPageInfo({ ...pageInfo, currentPage: pageNumber, isPagerChange: true });
  };

  const setIsRefreshPage = () => {
    setPageInfo({ ...pageInfo, isRefreshPage: false });
  };

  const onPageLimitChanged = (evt: any) => {
    let pages: number;
    setPageInfo({
      ...pageInfo,
      currentPage: 1,
      totalPages: pageInfo.totalPages,
      isPagerChange: false,
    });
    setPageLimit({
      pageLimit: parseInt(evt.target.value),
      isPageLimitChange: true,
    });
    setUserSetting(userSettingEnum.UserLimitConfig, parseInt(evt.target.value));
  };

  const handleSortingChange = (accessor: string) => {
    let sortOrder = sorting.orderBy;
    if (accessor === sorting.column) {
      sortOrder = sorting.orderBy === "asc" ? "desc" : "asc";
    }
    setSorting({ column: accessor, orderBy: sortOrder });
  };

  /**
   * This function calls the "getAllGroup" store funtion that get configuration list from API
   */
  const callGetHeadquarterManagementListService = () => {
    GetAllHeadquarterListService(
      pageInfo.currentPage,
      pageLimitInfo.pageLimit,
      Number(businessTypeSearch)
    );
  };

  useEffect(() => {
    callGetHeadquarterManagementListService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessTypeSearch]);

  useEffect(() => {
    GetAllBusinessTypeService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "language" of the useEffect changed.
   */
  useEffect(() => {
    if (islanguageChanged) {
      callGetHeadquarterManagementListService();
      updateLanguageChangedFlag();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [islanguageChanged]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "GroupList" of the useEffect changed.
   */
  useEffect(() => {
    setPageInfo({
      ...pageInfo,
      totalPages: headquarterList?.PagingDetails?.TotalPages,
      isPagerChange: false,
      isRefreshPage: false,
    });
    setDisablePagination(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allHeadquarterList]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "pageInfo.currentPage" of the useEffect changed.
   */
  useEffect(() => {
    if (pageInfo.isPagerChange) {
      callGetHeadquarterManagementListService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageInfo.currentPage]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateGroupstate.success" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateHeadquartertState.success) {
      resetAddUpdateHeadquarter();
      callGetHeadquarterManagementListService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addUpdateHeadquartertState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "deleteStoreManagementSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (deleteHeadquarterByIdState.success) {
      resetDeleteHeadquarter();
      if (allHeadquarterList?.length == 1) {
        if (pageInfo.currentPage > 1)
          setPageInfo({
            ...pageInfo,
            currentPage: 1,
            isRefreshPage: true,
            isPagerChange: true,
          });
        else callGetHeadquarterManagementListService();
      } else callGetHeadquarterManagementListService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteHeadquarterByIdState.success]);

  /**
   * This function shows the add user pop up from where we can add new user and update existing user. we use id to identify
   * that we are adding or updating user.
   * "Id == -1" : we are adding new user
   * "Id != -1" : means we are updating existing user
   */
  const openAddHeadquarterManagementHandler = (row: { Id: number }) => {
    setSelectedId(row.Id);
    props.addToggleHandler();
  };

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "pageLimitInfo.pageLimit" of the useEffect changed.
   */
  useEffect(() => {
    if (pageLimitInfo.isPageLimitChange) {
      callGetHeadquarterManagementListService();
      setPageLimit({ ...pageLimitInfo, isPageLimitChange: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLimitInfo.pageLimit]);

  /**
   * This function provides manual refresh functionality
   */
  const refreshClickHandler = () => {
    callGetHeadquarterManagementListService();
  };

  const changePasswordClickHandler = (row: { Id: number }) => {
    setSelectedId(row.Id);
    props.changePasswordToggleHandler();
  };

  const onSubmitForm = (values: any) => {
    let submitData: any;
    submitData = {
      HeadquarterId: selectedId,
      UserType: userTypeEnum.HeadQuarter,
      NewPassword: safeBase64Encode(values.NewPassword),
    };

    UpdateHeadquarterPasswordService(submitData);
  };

  const columArray: any = [
    {
      columnType: ColumnType.Hidden,
      accessorKey: "Id",
    },
    {
      columnType: ColumnType.JsxElement,
      jsxElementFormatter: (info: CellContext<any, any>) => {
        const row = info.row.original;
        return (
          <>
            <p>{row.HeadquarterName}</p>
            <small className="text-muted">{row.HeadquarterName}</small>
          </>
        );
      },
      accessorKey: "HeadquarterName",
      displayName: "headquarter_name",
      // sortingName: "headquarter_name",
    },
    {
      columnType: ColumnType.TextHeader,
      accessorKey: "Email",
      displayName: "email",
      sortingName: "email",
    },
    {
      columnType: ColumnType.JsxElement,
      jsxElementFormatter: (info: CellContext<any, any>) => {
        const row = info.row.original;
        return (
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              openAddHeadquarterManagementHandler(row);
            }}
          >
            <i className="bi bi-eye me-2"></i>
            <FormattedMessage id="see_details" />
          </Button>
        );
      },
      accessorKey: "isEdit",
      displayName: "",
      headerClassName: "last-column",
    },
    {
      columnType: ColumnType.JsxElement,
      jsxElementFormatter: (info: CellContext<any, any>) => {
        const row = info.row.original;
        return (
          <>
            {isOperationPermittedRoleBase(
              ModuleType.HeadquarterManagement,
              ActionType.Edit
            ) && (
              <Button
                className="me-2"
                variant="secondary"
                size="sm"
                onClick={() => {
                  changePasswordClickHandler(row);
                }}
                title={formatMessage("change_password")}
              >
                <i className="bi bi-lock"></i>
                {/* <FormattedMessage id="see_details" /> */}
              </Button>
            )}
          </>
        );
      },
      accessorKey: "isChangePassword",
      displayName: "",
      headerClassName: "last-column",
    },
  ];
  const columns = getColumns<IHeadquarterVM, number>(
    columArray,
    sorting,
    handleSortingChange
  );

  return (
    <React.Fragment>
      {props.showAddPopup && (
        <AddEditHeadquarter
          id={selectedId}
          modalClosed={props.addToggleHandler}
        />
      )}

      {props.showChangePasswordPopup && (
        <ChangePassword
          id={selectedId}
          modalClosed={props.changePasswordToggleHandler}
          submitHandler={onSubmitForm}
          storeCode={""}
        />
      )}

      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-6">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="headquarter_management" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <FormattedMessage id="master_management" />
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  <FormattedMessage id="headquarter_management" />
                </li>
              </ol>
            </div>
          </div>
          <div className="col-6">
            <ul className="add-list">
              <li>
                <LanguageSlider />
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="container-fluid mt-4 p-0 mb-2">
        <div className="row">
          <div className="col-lg-8 search_box">
            <div className="row">
              <div className="col-md-4">
                <div className="input-group">
                  <div className="input-group-text">
                    <span role="button" onClick={() => {}}>
                      <FontAwesomeIcon icon={faSearch} />
                    </span>
                  </div>
                  <DropdownInput
                    className="form-control"
                    placeholder={formatMessage("search_by_business_type")}
                    value={businessTypeSearch}
                    disabled={
                      userType === userTypeEnum[userTypeEnum.HeadQuarter]
                    }
                    onChange={handleBusinessTypeSearch}
                    options={getAllBusinessTypesAll?.map((option: any) => ({
                      label: option.value,
                      value: option.id,
                    }))}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="d-flex justify-content-end align-items-center">
              {getHeadquarterFilter.IsFilterActive && (
                <button
                  className={`ms-3 btn ${
                    getHeadquarterFilter.IsFilterActive
                      ? "btn-primary"
                      : "btn-outline-secondary"
                  }`}
                  onClick={() => {
                    resetHeadquarterFilterDetail();
                    setBusinessTypeSearch(-1);
                    setPageInfo({...pageInfo, currentPage: 1, isRefreshPage: true});
                  }}
                  title={formatMessage(
                    getHeadquarterFilter.IsFilterActive
                      ? "clear_filter"
                      : "apply_filter"
                  )}
                >
                  <svg
                    id="fi_3126668"
                    enableBackground="new 0 0 512 512"
                    height="18"
                    viewBox="0 0 512 512"
                    width="18"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="#ffffff"
                  >
                    <g>
                      <path d="m215.546 85.327h-162.264c-18.073 0-28.679 20.379-18.31 35.187.133.199-3.448-4.682 130.024 177.006 5.921 8.587 4.149-.599 4.149 190.987 0 19.245 21.993 30.358 37.542 18.791 57.536-43.372 71.516-48.257 71.516-70.955 0-133.909-1.721-130.311 4.149-138.823l27.851-37.923c-70.082-25.496-112.087-99.608-94.657-174.27z"></path>
                      <path d="m281.951 30.166c-75.076 67.31-38.685 187.35 55.962 206.05 75.479 15.948 143.193-43.867 143.193-116.945 0-102.594-122.364-157.159-199.155-89.105zm118.529 106.804c9.515 9.466 2.715 25.676-10.603 25.676-8.014 0-10.022-3.79-28.462-22.158-18.064 17.984-20.27 22.158-28.472 22.158-13.349 0-20.063-16.264-10.603-25.676l17.769-17.699-17.769-17.699c-14.107-14.035 7.142-35.322 21.216-21.297l17.859 17.779 17.849-17.779c14.074-14.025 35.331 7.254 21.216 21.297l-17.769 17.699z"></path>
                    </g>
                  </svg>
                </button>
              )}
              <button
                className="btn btn-primary ms-3"
                onClick={() => {
                  openAddHeadquarterManagementHandler({ Id: -1 });
                }}
              >
                <i className="bi bi-plus-lg me-1"></i>
                {formatMessage("add_headquarter")}
              </button>
            </div>
          </div>
        </div>
      </div>
      {allHeadquarterList?.length > 0 ? (
        <div className={`${openPanel ? "panelOpen" : ""}`}>
          <div>
            <div className="card changingPanel">
              <div className="tableDesign row-eq-height tableDevices">
                <div
                  className="table-responsive hqListTable"
                  data-testid="table"
                >
                  {allHeadquarterList && (
                    <Grid
                      data={allHeadquarterList}
                      columns={columns}
                      isInProgress={getAllHeadquartertListState.inProgress}
                      hiddenColumns={["Id"]}
                    />
                  )}
                  {(getAllHeadquartertListState.inProgress ||
                    masterManagementStore.inProgress) && (
                    <FormLoader
                      loading={
                        getAllHeadquartertListState.inProgress ||
                        masterManagementStore.inProgress
                      }
                    />
                  )}
                </div>
              </div>
            </div>
            {allHeadquarterList?.length > 0 ? (
              <div className="d-flex align-items-center justify-content-center p-2 tableFooter">
                <Pagination
                  isRefreshPage={pageInfo.isRefreshPage}
                  totalPages={pageInfo.totalPages}
                  pageLimit={pageLimitInfo.pageLimit}
                  onPageChanged={onPageChanged}
                  disablePagination={disablePagination}
                  setIsRefreshPage={setIsRefreshPage}
                />

                <select
                  data-testid="pageSelect"
                  className="pageLimit ms-1"
                  onChange={onPageLimitChanged}
                  value={pageLimitInfo.pageLimit}
                >
                  {pageLimitOptions.map((op) => (
                    <option key={op} value={op}>
                      {op} {formatMessage("rows")}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              ""
            )}
          </div>
        </div>
      ) : (
        <div className="message text-center">
          <img src={noMessageIcon} alt="" className="no_message" />
          {businessTypeSearch !== 0 ? (
            <>
              <p className="noData text-muted text-center h4 mb-0">
                <FormattedMessage id="no_data_available" />
              </p>
            </>
          ) : (
            <>
              <p className="noData text-muted text-center h4 mb-0">
                <FormattedMessage id="no_store" />
              </p>
              <p className="mb-3">{formatMessage("no_store_message")}</p>
              {isOperationPermittedRoleBase(
                ModuleType.HeadquarterManagement,
                ActionType.Add
              ) && (
                <LoaderButton
                  text="add_store"
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                    openAddHeadquarterManagementHandler({ Id: -1 })
                  }
                />
              )}
            </>
          )}
        </div>
      )}
    </React.Fragment>
  );
};

export default ChangePasswordPopupHOC(AddPopupHOC(observer(HeadQuarterList)));
